import { LowStockLotField } from "../components/low-stock-lot-field";
import { LowStockIndicator } from "../components/low-stock-alerts";
//seed-page.tsx

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import { useEffect, useState, useContext } from "react";
import Title from "../components/title";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Radio,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LoadingBackdrop from "../components/loading-backdrop";
import { ErrorContext, UserContext } from "../components/app-routes";
import { seedsApi } from "../services/api/seeds";
import { Seed, SeedLot } from "../models/seed";
import { Grower } from "../models/grower";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import SeedForm, { SeedFormValues } from "../components/seed-form";
import { StripedDataGrid } from "../components/striped-data-grid";
import { formatCurrency, toCurrency } from "./currency";
import {
  GridActionsCellItem,
  GridToolbar,
  GridRowClassNameParams,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import * as yup from "yup";
import { Formik, Form, FastField, FieldProps, Field, FieldArray } from "formik";
import { AppTextField } from "../components/text-field";
import { validationMessages } from "../utils/validation/messages";
import { seedLotsApi } from "../services/api/seed-lots";
import ConfirmDialog from "../components/confirm-dialog";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getCustomLabel } from "../utils/custom-labels";

export interface SeedLotFormValues {
  number: string;
  supplier: string;
  archived: boolean;
  isMix: boolean;
  seedMixComponents: {
    seedLotId: string;
    proportion: string;
  }[];
  notes: string;
  dateReceived: string | null;
  qtyReceivedKg: number | null;
  costOfLot: number | null;
  qtyReceivedUnit: string;
  invoiceNumber: string;
  lowStockThreshold?: number | null;
}

const SeedLotDialog = (props: {
  open: boolean;
  actionLabel: string;
  grower: Grower | undefined;
  seedLots: SeedLot[];
  initialValues: SeedLotFormValues;
  onSubmit: (values: SeedLotFormValues) => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog open={props.open} maxWidth={"sm"} fullWidth>
      <Formik
        initialValues={props.initialValues}
        validationSchema={yup.object({
          number: yup.string().required(validationMessages.Required),
          supplier: yup.string(),
          archived: yup.boolean(),
          notes: yup.string(),
          seedMixComponents: yup.array().when("isMix", {
            is: true,
            then: yup
              .array()
              .test(
                "seed-mix-total",
                "Seed Mix proportions must total 100%.",
                (components) => {
                  const total = (components || []).reduce((sum, component) => {
                    return sum + Number(component.proportion || 0);
                  }, 0);

                  return total === 100;
                },
              ),
          }),
        })}
        onSubmit={props.onSubmit}
      >
        {(form) => (
          <>
            <Form noValidate>
              <DialogTitle>Seed Lot</DialogTitle>
              <DialogContent>
                <Box>
                  <FormControl
                    variant="standard"
                    error={
                      form.touched.archived && Boolean(form.errors.archived)
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="archived"
                          color="primary"
                          onChange={form.handleChange}
                          checked={form.values.archived}
                        />
                      }
                      label="Archived"
                    />

                    {form.touched.archived && form.errors.archived && (
                      <FormHelperText>{form.errors.archived}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <FastField name="number">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      autoFocus
                      label="Lot Number"
                    />
                  )}
                </FastField>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isMix"
                      color="primary"
                      onChange={(e) => {
                        form.setFieldValue("isMix", e.target.checked);
                      }}
                      checked={form.values.isMix}
                    />
                  }
                  label="This is a Custom Seed Mix"
                />
                {!form.values.isMix && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FastField
                        component={AppTextField}
                        name="supplier"
                        label="Supplier"
                        autoComplete="new-password"
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FastField
                        component={AppTextField}
                        name="invoiceNumber"
                        label="Invoice #"
                        autoComplete="new-password"
                      />
                    </Box>
                  </Box>
                )}
                {props.grower?.Plan === "pro" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Field
                        key={
                          form.values.isMix ? "date-created" : "date-received"
                        }
                        component={AppTextField}
                        name="dateReceived"
                        label={
                          form.values.isMix ? "Date Created" : "Date Received"
                        }
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    {!form.values.isMix && (
                      <Box sx={{ flex: 1 }}>
                        <FastField
                          component={AppTextField}
                          name="costOfLot"
                          label={`Cost of Lot (${props.grower?.CountryISO || ""})`}
                          type="number"
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {form.values.isMix && (
                  <FieldArray
                    name="seedMixComponents"
                    render={(arrayHelpers) => (
                      <Box>
                        {form.values.seedMixComponents.map((component, i) => (
                          <Box key={i} sx={{ display: "flex", gap: 2 }}>
                            <Box sx={{ flex: 3 }}>
                              <FastField
                                component={AppTextField}
                                name={`seedMixComponents[${i}].seedLotId`}
                                label={`Seed ${i + 1}`}
                                select
                              >
                                <MenuItem value="">Select seed lot</MenuItem>
                                {props.seedLots
                                  .filter((lot) => lot.archivedAt === null)
                                  .sort((a, b) =>
                                    `${a.seed?.name || ""} ${a.number}`.localeCompare(
                                      `${b.seed?.name || ""} ${b.number}`,
                                    ),
                                  )
                                  .map((lot) => (
                                    <MenuItem
                                      key={lot.id}
                                      value={lot.id.toString()}
                                    >
                                      {`${lot.seed?.variety ? `${lot.seed.variety} ` : ""}${lot.seed?.name || "Seed"} - ${lot.number}`}{" "}
                                    </MenuItem>
                                  ))}
                              </FastField>
                              {component.seedLotId && (
                                <FormHelperText>
                                  Available:{" "}
                                  {(
                                    props.seedLots.find(
                                      (lot) =>
                                        lot.id.toString() ===
                                        component.seedLotId,
                                    )?.currentStockKg || 0
                                  ).toFixed(1)}{" "}
                                  kg
                                </FormHelperText>
                              )}
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <FastField
                                component={AppTextField}
                                name={`seedMixComponents[${i}].proportion`}
                                label="%"
                                type="number"
                              />
                              <FormHelperText>
                                Uses:{" "}
                                {(
                                  Number(form.values.qtyReceivedKg || 0) *
                                  (Number(component.proportion || 0) / 100)
                                ).toFixed(1)}{" "}
                                kg
                              </FormHelperText>
                            </Box>
                            <Box
                              sx={{
                                width: { xs: "100%", sm: "auto" },
                                display: "flex",
                                justifyContent: {
                                  xs: "flex-end",
                                  sm: "initial",
                                },
                              }}
                            >
                              <IconButton
                                aria-label="Remove seed lot"
                                onClick={() => {
                                  arrayHelpers.remove(i);
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        <FormHelperText>
                          Total:{" "}
                          {form.values.seedMixComponents.reduce(
                            (sum, component) => {
                              return sum + Number(component.proportion || 0);
                            },
                            0,
                          )}
                          %
                        </FormHelperText>

                        {typeof form.errors.seedMixComponents === "string" && (
                          <FormHelperText error>
                            {form.errors.seedMixComponents}
                          </FormHelperText>
                        )}

                        <Button
                          sx={{ mt: 2, mb: 4 }}
                          color="secondary"
                          variant="contained"
                          onClick={() =>
                            arrayHelpers.push({
                              seedLotId: "",
                              proportion: "",
                            })
                          }
                        >
                          Add Seed
                        </Button>
                      </Box>
                    )}
                  />
                )}
                {props.grower?.Plan === "pro" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 3 }}>
                      <Field
                        key={form.values.isMix ? "qty-created" : "qty-received"}
                        component={AppTextField}
                        name="qtyReceivedKg"
                        label={
                          form.values.isMix ? "Qty Created" : "Qty Received"
                        }
                        type="number"
                      />
                    </Box>

                    <Box sx={{ flex: 3 }}>
                      <FastField
                        component={AppTextField}
                        name="qtyReceivedUnit"
                        label="Unit"
                        select
                      >
                        <MenuItem value="kg">kg</MenuItem>
                        <MenuItem value="g">g</MenuItem>
                        <MenuItem value="lb">lb</MenuItem>
                        <MenuItem value="oz">oz</MenuItem>
                      </FastField>
                    </Box>
                  </Box>
                )}

                {props.grower?.Plan === "pro" && (
                  <LowStockLotField
                    seed={true}
                    defaultUnit={
                      props.grower.UnitSystem === "imperial" ? "lb" : "kg"
                    }
                  />
                )}
                <FastField name="notes">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      multiline
                      label="Notes"
                      rows={4}
                    />
                  )}
                </FastField>
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" onClick={props.onCancel}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  {props.actionLabel}
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default function SeedPage() {
  const context = useContext(UserContext);
  const grower = context.grower;
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState<Seed | null>(null);
  const [seedLots, setSeedLots] = useState<SeedLot[] | null>(null);
  const [allSeedLots, setAllSeedLots] = useState<SeedLot[] | null>(null);
  const [seedLotChangeId, setSeedLotChangeId] = useState<number>(0);
  const [saved, setSaved] = useState(false);
  const [showAddLotDialog, setShowAddLotDialog] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [openConfirmTrashSeedLotDialog, setOpenConfirmTrashSeedLotDialog] =
    useState(false);
  const [seedLotToAction, setSeedLotToAction] = useState<SeedLot | null>(null);
  const [openEditLotDialog, setOpenEditLotDialog] = useState(false);

  const showArchivedSeedLotsCheckedStorageKey = "seedLotsShowArchivedChecked";
  const showActiveSeedLotsCheckedStorageKey = "seedLotsShowActiveChecked";

  const [showArchivedSeedLotsChecked, setShowArchivedSeedLotsChecked] =
    useState(
      window.localStorage.getItem(showArchivedSeedLotsCheckedStorageKey) !==
        false.toString(),
    );

  const [showActiveSeedLotsChecked, setShowActiveSeedLotsChecked] = useState(
    window.localStorage.getItem(showActiveSeedLotsCheckedStorageKey) !==
      false.toString(),
  );

  useEffect(() => {
    async function getSeed() {
      setBusy(true);
      try {
        const seed = await seedsApi.getSingle(parseInt(id));
        setSeed(seed);
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }
    getSeed();
  }, [id]);

  useEffect(() => {
    async function getSeedLots() {
      if (!seed) {
        return;
      }

      setBusy(true);
      try {
        const seedLots = await seedLotsApi.getAll(seed);
        setSeedLots(seedLots);
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }
    getSeedLots();
  }, [seed, seedLotChangeId]);

  useEffect(() => {
    async function getAllSeedLots() {
      setBusy(true);
      try {
        const allSeeds = await seedsApi.getAll();

        const lots = (
          await Promise.all(
            allSeeds.map(async (s) => {
              const lots = await seedLotsApi.getAll(s);

              return lots.map((lot) => ({
                ...lot,
                seed: s,
              }));
            }),
          )
        ).flat();

        setAllSeedLots(lots);
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }

    getAllSeedLots();
  }, []);

  const handleSubmit = async (values: SeedFormValues) => {
    if (!seed) {
      throw new Error("Invalid state");
    }

    setBusy(true);

    try {
      await seedsApi.update({
        ...seed,
        name: values.name,
        variety: values.variety,
        notes: values.notes,
        archivedAt:
          !!seed.archivedAt === values.archived
            ? seed.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
      });

      setSaved(true);

      mixpanel.track("Update Seed");

      goBackOrTo(history, "/home/seeds");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const onClickCancel = () => {
    goBackOrTo(history, "/home/seeds");
  };

  const onClickAddLot = () => {
    setShowAddLotDialog(true);
  };

  const handleShowActiveSeedLotsChanged = (checked: boolean) => {
    setShowActiveSeedLotsChecked(checked);
    window.localStorage.setItem(
      showActiveSeedLotsCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedSeedLotsChanged = (checked: boolean) => {
    setShowArchivedSeedLotsChecked(checked);
    window.localStorage.setItem(
      showArchivedSeedLotsCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickSetDefaultSeedLot = async (seedLotId: number) => {
    if (!seed || !seedLots) {
      return;
    }

    const seedLot = seedLots.find((lot) => lot.id === seedLotId);

    if (!seedLot) {
      return;
    }

    try {
      await seedLotsApi.update(seed, {
        ...seedLot,
        isDefault: true,
      });

      setSeedLotChangeId(seedLotChangeId + 1);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmitAddLotDialog = async (values: SeedLotFormValues) => {
    if (!seed) {
      throw new Error("Invalid state");
    }

    if (!grower) {
      throw new Error("Invalid UserContext");
    }
    setBusy(true);

    try {
      const activeSeedLots =
        seedLots?.filter((l) => l.archivedAt === null) || [];
      const defaultSeedLot = activeSeedLots.find((l) => l.isDefault);

      if (activeSeedLots.length === 1 && !defaultSeedLot) {
        await seedLotsApi.update(seed, {
          ...activeSeedLots[0],
          isDefault: true,
        });
      }
      await seedLotsApi.add(seed, {
        id: 0,
        createdAt: null,
        number: values.number,
        supplier: values.supplier,
        invoiceNumber: values.invoiceNumber,
        lowStockThreshold: values.lowStockThreshold ?? null,
        archivedAt: values.archived ? DateTime.now().toISO() : null,
        notes: values.notes,
        isDefault: false,
        isMix: values.isMix,
        mixComponents: values.isMix
          ? values.seedMixComponents.map((c) => ({
              id: 0,
              seedLotId: Number(c.seedLotId),
              percent: Number(c.proportion),
              quantityUsedKg: 0,
            }))
          : [],
        dateReceived: values.dateReceived
          ? DateTime.fromISO(values.dateReceived).toISO()
          : null,
        qtyReceivedKg:
          values.qtyReceivedKg === null || values.qtyReceivedKg === undefined
            ? null
            : Number(values.qtyReceivedKg),
        costOfLot:
          values.costOfLot != null
            ? toCurrency(values.costOfLot.toString(), grower.CountryISO)
                .intValue
            : null,
      });

      setSeedLotChangeId(seedLotChangeId + 1);
      setShowAddLotDialog(false);

      mixpanel.track("Update Seed Lot");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const onCloseTrashSeedLotDialog = async (ok: boolean | undefined) => {
    if (!seed || !seedLotToAction) {
      throw new Error("Invalid state");
    }

    setOpenConfirmTrashSeedLotDialog(false);

    if (!ok) {
      return;
    }

    setBusy(true);

    try {
      await seedLotsApi.trash(seed, seedLotToAction);
      setSeedLotChangeId(seedLotChangeId + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setSeedLotToAction(null);
      setBusy(false);
    }
  };

  const onClickTrashSeedLot = (id: number) => {
    if (!seedLots) {
      throw new Error("Invalid state");
    }

    const seedLot = seedLots.find((v) => v.id === id);

    if (!seedLot) {
      throw new Error("Invalid state");
    }

    setSeedLotToAction(seedLot);
    setOpenConfirmTrashSeedLotDialog(true);
  };

  const onSubmitEditLotDialog = async (values: SeedLotFormValues) => {
    if (!seed || !seedLotToAction) {
      throw new Error("Invalid state");
    }

    setOpenEditLotDialog(false);

    if (!grower) {
      throw new Error("Invalid UserContext");
    }
    setBusy(true);

    try {
      await seedLotsApi.update(seed, {
        ...seedLotToAction,
        number: values.number,
        supplier: values.supplier,
        invoiceNumber: values.invoiceNumber,
        lowStockThreshold: values.lowStockThreshold ?? null,
        archivedAt:
          seedLotToAction.archivedAt && values.archived
            ? seedLotToAction.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
        dateReceived: values.dateReceived
          ? DateTime.fromISO(values.dateReceived).toISO()
          : null,
        qtyReceivedKg:
          values.qtyReceivedKg === null || values.qtyReceivedKg === undefined
            ? null
            : Number(values.qtyReceivedKg),
        costOfLot:
          values.costOfLot != null
            ? toCurrency(values.costOfLot.toString(), grower.CountryISO)
                .intValue
            : null,
        notes: values.notes,
        isMix: values.isMix,
        mixComponents: values.isMix
          ? values.seedMixComponents.map((c) => ({
              id: 0,
              seedLotId: Number(c.seedLotId),
              percent: Number(c.proportion),
              quantityUsedKg: 0,
            }))
          : [],
      });
      setSeedLotChangeId(seedLotChangeId + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setSeedLotToAction(null);
      setBusy(false);
    }
  };

  const onClickEditSeedLot = (id: number) => {
    if (!seedLots) {
      throw new Error("Invalid state");
    }

    const seedLot = seedLots.find((v) => v.id === id);

    if (!seedLot) {
      throw new Error("Invalid state");
    }

    setSeedLotToAction(seedLot);
    setOpenEditLotDialog(true);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            overflow: "auto",
            flexDirection: "column",
          }}
        >
          <Title>{getCustomLabel(grower ?? null, "seeds")}</Title>{" "}
          {seed && seedLots && (
            <>
              <SeedForm
                initialValues={{
                  archived: !!seed.archivedAt,
                  id: seed.id,
                  name: seed.name,
                  notes: seed.notes,
                  variety: seed.variety,
                }}
                saved={saved}
                onSubmit={handleSubmit}
              >
                <Box mt={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={onClickCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={busy}
                  >
                    Save
                  </Button>
                </Box>
              </SeedForm>
              <Box mt={2}>
                <Typography variant="h5" color="black" gutterBottom>
                  Lots
                </Typography>
                <Button
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={onClickAddLot}
                >
                  Add lot
                </Button>
                <ActiveFilterControls
                  showActiveChecked={showActiveSeedLotsChecked}
                  showArchivedChecked={showArchivedSeedLotsChecked}
                  onChangeShowActive={handleShowActiveSeedLotsChanged}
                  onChangeShowArchived={handleShowArchivedSeedLotsChanged}
                />
                {seedLots.length > 0 && (
                  <StripedDataGrid
                    storageKey="seed-page-grid"
                    columns={[
                      {
                        field: "isDefault",
                        headerName: "Default",
                        width: 80,
                        sortable: false,
                        renderCell: (params: GridRenderCellParams) => (
                          <Radio
                            checked={Boolean(params.row.isDefault)}
                            disabled={Boolean(params.row.archived)}
                            onChange={() =>
                              onClickSetDefaultSeedLot(params.row.id)
                            }
                          />
                        ),
                      },
                      {
                        field: "number",
                        renderCell: (params: GridRenderCellParams) => (
                          <span>
                            {params.value}
                            <LowStockIndicator
                              kind="seed"
                              lotId={Number(params.id)}
                            />
                          </span>
                        ),
                        headerName: "Lot Number",
                        width: 200,
                      },
                      {
                        field: "supplier",
                        headerName: "Supplier",
                        width: 200,
                      },
                      {
                        field: "archived",
                        headerName: "Archived",
                        width: 100,
                        type: "boolean",
                      },
                      {
                        field: "createdAt",
                        headerName: "Created",
                        width: 200,
                        type: "dateTime",
                        renderCell: (params: GridRenderCellParams) => {
                          const value = params.value as Date | string | null;
                          if (!value) return "";

                          const dateObj =
                            typeof value === "string"
                              ? DateTime.fromISO(value)
                              : DateTime.fromJSDate(value);

                          return dateObj.toLocaleString(DateTime.DATETIME_MED);
                        },
                      },
                      {
                        field: "notes",
                        headerName: "Notes",
                        width: 200,
                        type: "string",
                      },
                      {
                        field: "actions",
                        type: "actions",
                        headerName: "",
                        width: 100,
                        minWidth: 100,
                        cellClassName: "actions",
                        flex: 1,
                        align: "right",
                        hideable: false,
                        getActions: (params: GridRowParams) => [
                          <GridActionsCellItem
                            icon={<EditIcon />}
                            label="Edit"
                            className="textPrimary"
                            onClick={() => onClickEditSeedLot(params.row.id)}
                            color="inherit"
                          />,
                        ],
                      },
                    ]}
                    rows={seedLots
                      .filter((c) =>
                        c.archivedAt === null
                          ? showActiveSeedLotsChecked
                          : showArchivedSeedLotsChecked,
                      )
                      .map((c) => ({
                        id: c.id,
                        number: c.number,
                        supplier: c.supplier,
                        archived: c.archivedAt ? true : false,
                        createdAt: DateTime.fromISO(c.createdAt!).toJSDate(),
                        notes: c.notes,
                        isDefault:
                          c.isDefault ||
                          (c.archivedAt === null &&
                            seedLots.filter((l) => l.archivedAt === null)
                              .length === 1),
                      }))}
                    initialState={{
                      filter: undefined,
                      sorting: {
                        sortModel: [
                          {
                            field: "createdAt",
                            sort: "desc",
                          },
                        ],
                      },
                    }}
                    slots={{
                      toolbar: GridToolbar,
                    }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        csvOptions: {
                          allColumns: true,
                        },
                      },
                    }}
                    getRowClassName={(params: GridRowClassNameParams) => {
                      if (params.row.archived) {
                        return "archived-row";
                      }

                      return params.indexRelativeToCurrentPage % 2 === 0
                        ? "even"
                        : "odd";
                    }}
                    disableRowSelectionOnClick
                  />
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>
      <LoadingBackdrop open={busy} />
      <SeedLotDialog
        actionLabel="Add"
        grower={grower}
        seedLots={allSeedLots || []}
        initialValues={{
          archived: false,
          number: "",
          supplier: "",
          invoiceNumber: "",
          isMix: false,
          seedMixComponents: [
            {
              seedLotId: "",
              proportion: "",
            },
            {
              seedLotId: "",
              proportion: "",
            },
          ],
          notes: "",
          dateReceived: null,
          qtyReceivedKg: null,
          qtyReceivedUnit: "kg",
          costOfLot: null,
        }}
        onCancel={() => setShowAddLotDialog(false)}
        open={showAddLotDialog}
        onSubmit={onSubmitAddLotDialog}
      />
      {seedLotToAction && (
        <SeedLotDialog
          actionLabel="Save"
          grower={grower}
          seedLots={allSeedLots || []}
          initialValues={{
            archived: !!seedLotToAction.archivedAt,
            number: seedLotToAction.number,
            supplier: seedLotToAction.supplier,
            invoiceNumber: seedLotToAction.invoiceNumber,
            lowStockThreshold: seedLotToAction.lowStockThreshold ?? null,
            isMix: seedLotToAction.isMix || false,
            seedMixComponents: seedLotToAction.mixComponents
              ? seedLotToAction.mixComponents.map((component) => ({
                  seedLotId: component.seedLotId.toString(),
                  proportion: component.percent.toString(),
                }))
              : [],
            notes: seedLotToAction.notes,
            dateReceived: seedLotToAction.dateReceived
              ? DateTime.fromISO(seedLotToAction.dateReceived).toISODate()
              : null,
            qtyReceivedKg: seedLotToAction.qtyReceivedKg,
            qtyReceivedUnit: "kg",
            costOfLot:
              seedLotToAction.costOfLot != null && grower
                ? toCurrency(seedLotToAction.costOfLot, grower.CountryISO).value
                : null,
          }}
          onCancel={() => setOpenEditLotDialog(false)}
          open={openEditLotDialog}
          onSubmit={onSubmitEditLotDialog}
        />
      )}
      <ConfirmDialog
        open={openConfirmTrashSeedLotDialog}
        description={`Are you sure you want to trash the seed lot '${seedLotToAction?.number}'?`}
        onClose={onCloseTrashSeedLotDialog}
        title="Trash Seed Lot"
      />
    </Container>
  );
}
